<template>
  <div class="project-leads-view-list" style="display: contents">
    <DataTable
      v-if="items.length"
      sticky="var(--header-height)"
      :selected.sync="selected"
      :sort-by="sort"
      @update:sortBy="$ev => $emit('update:sort', $ev)"
      :sortable-by="sortableBy.map(s => s.key)"
      :value="items"
      :columns="[
        { id: 'contact.name', label: $t('name') },
        { id: 'status', label: $t('status') },
        { id: 'scopes', label: $t('leadReferences') },
        { id: 'type', label: $tc('type') },
        { id: 'tag', label: $tc('tag', 2) },
        { id: 'referrer', label: $tc('leadReferrer') },
        { id: 'createdAt', label: $t('createdAt'), align: 'right' },
      ]"
      :row-wrapper="
        lead => ({
          component: 'router-link',
          props: {
            to: { name: leadRouteName, params: { id: lead.id, reference: $route.params.reference } },
            style: { color: 'inherit' },
          },
        })
      "
    >
      <template #contact.name="{ item }">
        <ContactCard :value="item.contact" theme="list" />
      </template>
      <template #status="{ item }">
        <Badge
          v-if="!getPipelineColumn(item).isFallback"
          class="project-leads-view-list__status"
          :value="getPipelineColumn(item).label"
          :style="`--c-h: ${getPipelineColumn(item).color.hsl.h}; --c-s: ${getPipelineColumn(item).color.hsl.s}; --c-l: ${getPipelineColumn(item).color.hsl.l}; `"
        />
        <span v-else>-</span>
      </template>
      <template #type="{ item }">
        <Badge :value="$tc(item.contact.type, 1)" :theme="item.contact.type == 'customer' ? 'secondary' : 'default'" />
      </template>
      <template #tag="{ item }">
        <div class="row g-1">
          <Badge v-for="tag in getTags(item)" :key="tag" :value="tag" />
        </div>
      </template>
      <template #scopes="{ item }">
        {{ getScopes(item) }}
      </template>
      <template #referrer="{ item }">
        <Referrers :value="item.referrer">
          <template #fallback>-</template>
        </Referrers>
      </template>
      <template #createdAt="{ item }">
        <small class="fade">{{ item.createdAt | Moment('L, LT') }}</small>
      </template>
      <template #select-actions>
        <ButtonComponent :label="$t('sendMessage')" :icon="['fal', 'comment']" @click="showModal = true" />
      </template>
    </DataTable>
    <CommentModal :show.sync="showModal" :promise="commentPromise" @onSuccess="showModal = false" @submit="comment">
      <div class="mb-2">
        <div class="mb-1">{{ $tc('receiver', 2) }}</div>
        <Avatar :value="selected.map(({ name, avatar: src }) => ({ src, name }))" :max="8" />
      </div>
    </CommentModal>
  </div>
</template>

<script>
import CommentModal from '@/components/Comment/Modal'
import CommentApi from '@/api/Comment'
import DataTable from '@/components/DataTable'
import { Moment } from '@/filters'
import { HexToHsl } from '@/utils'

import { FallbackColumn } from '@/components/Kanban/Component.vue'
import Badge from '@/components/Badge'
import ContactCard from '@/components/Contact/Card'
import Avatar from '@/components/Avatar'
import Referrers from '@/components/Lead/Referrers'
import { LeadPathByModel } from '@/components/Lead/utils'

import tracker from '@/tracking'

export default {
  props: {
    items: Array,
    sort: Object,
    sortableBy: Array,
    pipeline: Object,
    onModel: String,
  },
  data() {
    return {
      showModal: false,
      selected: [],
      commentPromise: null,
    }
  },
  computed: {
    leadRouteName() {
      return `${LeadPathByModel[this.onModel]}-lead`
    },
  },
  methods: {
    getTags(item) {
      return item.tags.filter(v => !this.$store.state.enums.SystemTags.some(i => new RegExp(i).test(v)))
    },
    getScopes(item) {
      if (!item || !item.scopes) return

      let residentials = item.scopes.filter(r => r.onModel === 'Residential').map(r => r.number)
      let project = item.scopes.find(r => r.onModel === 'Project')
      let result = []

      if (project) result.push(this.$tc('project'))
      if (residentials.length) result.push(`${this.$tc('residential', residentials.length)} ${residentials.join(', ')}`)

      return result.join(' + ')
    },
    getPipelineColumn(item) {
      const column = this.pipeline.columns.find(c => c.id === item.status) || FallbackColumn

      const hsl = HexToHsl(column.color)
      return {
        ...column,
        color: {
          hex: column.color,
          hsl: { h: hsl.h + 'deg', s: hsl.s + '%', l: hsl.l + '%' },
        },
      }
    },
    comment(data) {
      tracker.emit('multiple:comment', {})

      this.commentPromise = CommentApi.mutate(
        {
          data: this.selected.map(s => ({
            reference: { onModel: 'Lead', ref: s.id },
            ...data,
          })),
        },
        { mutation: 'CommentsCreate', transformPayload: false },
      )
    },
  },
  filters: {
    Moment,
  },
  components: {
    CommentModal,
    Badge,
    ContactCard,
    Avatar,
    DataTable,
    Referrers,
  },
}
</script>

<style lang="scss">
.project-leads-view-list {
  &__status {
    color: #000000 !important;
    background-color: hsla(var(--c-h), var(--c-s), var(--c-l), 5%) !important;
    border: 1px solid hsla(var(--c-h), var(--c-s), var(--c-l), 90%) !important;

    padding: 2px 9px;
  }

  .contact-card > .card__header {
    display: none;
  }
}
</style>
